import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import { request } from '../../Redux/Sagas/requests/api'
import { storePublicPageData } from '../../Redux/Reducers/publicPageData'
import { store } from '../../Redux/Config/configureStore'

const fetchPublicPageData = async (page_uuid) => {
  const res = await request(`get-page/${page_uuid}`)
  if (res && res.status === 200) {
    return res.data
  }
  throw new Error('Failed to fetch public page data')
}

/**
 * Loads public CMS page JSON once per mount (no refetch on focus / remount).
 * When Redux `refetchPublicPage` flips false → true (e.g. after CMS save), the query is invalidated once.
 */
export const useFetchPage = (page_uuid, refetchPublicPage) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const prevRefetchFlag = useRef(undefined)
  const prevPageUuid = useRef(page_uuid)

  const queryKey = ['publicPageData', page_uuid]

  useEffect(() => {
    if (prevPageUuid.current !== page_uuid) {
      prevPageUuid.current = page_uuid
      prevRefetchFlag.current = undefined
    }
    if (prevRefetchFlag.current === false && refetchPublicPage === true) {
      queryClient.invalidateQueries(queryKey)
    }
    prevRefetchFlag.current = refetchPublicPage
  }, [refetchPublicPage, page_uuid, queryClient])

  return useQuery(queryKey, () => fetchPublicPageData(page_uuid), {
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    onSuccess: (data) => {
      const latest = store.getState().publicPageData
      const newData = latest ? { ...latest } : {}
      newData[page_uuid] = data
      dispatch(storePublicPageData(newData))
    },
  })
}
